import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { SolPopoverMenuItemComponent } from '../sol-popover-menu-item/sol-popover-menu-item.component';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { AbstractComponent, FocusableComponent } from '../../abstract.component';
import { ShortcutService, SolShortcut, SolShortcutSubscription } from '../../../services/shortcut.service';
import { MenuTools } from '../../../tools/menu-tools';
import { TimerTools } from '../../../tools/timer-tools';
import { BrowserTools } from '../../../tools/browser-tools';
import { SolMenuGroup, SolPopoverMenuContentState } from './sol-popover-menu-content.state';

@Component({
  selector: 'sol-popover-menu-content',
  imports: [SolPopoverMenuItemComponent, TranslatePipe],
  templateUrl: './sol-popover-menu-content.component.html',
})
export class SolPopoverMenuContentComponent
  extends AbstractComponent<SolPopoverMenuContentState>
  implements AfterViewInit, OnDestroy
{
  // Dependencies
  private readonly shortcutService = inject(ShortcutService);

  // Computed
  protected readonly visibleGroups$ = this.compute(state => state.visibleGroups);
  protected readonly currentId$ = this.compute(state => state.currentId);
  protected readonly hostClass$ = this.compute(state => state.hostClass);
  protected readonly passive$ = this.compute(state => state.passive);

  // Events
  @Output('action')
  private readonly actionEmitter = new EventEmitter<string>();
  @Output('close')
  private readonly closeEmitter = new EventEmitter<void>();
  @Output('selected')
  private readonly selectedEmitter = new EventEmitter<string>();

  // Binding
  private host = inject(ElementRef);

  // Listener
  private _onAction: (actionId: string) => boolean = actionId => {
    this.actionEmitter.emit(actionId);
    return false;
  };
  private _close: () => void = () => this.closeEmitter.emit();

  // Cache
  private shortcutSubscription?: SolShortcutSubscription;

  constructor() {
    super(SolPopoverMenuContentState.of());

    this.watchComputed(
      state => state.shortcutsEnabled,
      () => TimerTools.nextTick(() => this.registerShortcuts()),
    );
  }

  ngAfterViewInit(): void {
    this.registerShortcuts();
  }

  ngOnDestroy(): void {
    this.shortcutSubscription?.unsubscribe();
  }

  @HostBinding('class')
  public get hostClass(): string {
    return this.hostClass$();
  }

  @Input()
  public set menu(menu: SolMenuGroup[]) {
    this.update(state => state.withMenu(menu));
  }

  @Input()
  public set shortcutsEnabled(shortcutsEnabled: boolean) {
    this.update(state => state.withShortcutsEnabled(shortcutsEnabled));
  }

  @Input()
  public set currentId(currentId: string) {
    this.update(state => state.withCurrentId(currentId));
  }

  /**
   * When true, the current item is highlighted but never grabs DOM focus. Used when an external
   * control owns focus (the query field's overlay input) and drives navigation from the outside.
   */
  @Input()
  public set passive(passive: boolean) {
    this.update(state => state.withPassive(passive));
  }

  public set onAction(onAction: (name: string) => boolean) {
    this._onAction = onAction;
  }

  public get onAction(): (actionId: string) => boolean {
    return this._onAction;
  }

  public set close(close: () => void) {
    this._close = close;
  }

  public get close(): () => void {
    return this._close;
  }

  protected onSelected(actionId: string, selected: boolean) {
    if (selected) {
      this.update(state => state.withCurrentId(actionId));
      this.selectedEmitter.emit(actionId);
    }
  }

  protected override focusable(): FocusableComponent | undefined {
    return BrowserTools.toFocusable(this.host);
  }

  private registerShortcuts() {
    // Unsubscribe
    this.shortcutSubscription?.unsubscribe();
    this.shortcutSubscription = undefined;

    // Enabled?
    if (!this.state.shortcutsEnabled) {
      return;
    }

    // Register shortcuts
    const actions = MenuTools.toShortcutActions(this.state.menu, actionId => this.onAction(actionId));
    this.shortcutSubscription = this.shortcutService.register('sol-popover-menu', true, this.host, [
      {
        shortcut: SolShortcut.of('Escape'),
        action: () => this.onEscape(),
      },
      {
        shortcut: SolShortcut.of('ArrowDown'),
        action: () => this.onArrowDown(),
      },
      {
        shortcut: SolShortcut.of('ArrowUp'),
        action: () => this.onArrowUp(),
      },
      {
        shortcut: SolShortcut.of('Enter'),
        action: () => this.onEnter(),
      },
      ...actions,
    ]);
  }

  private onEscape() {
    this.close();
    return true;
  }

  private onArrowDown() {
    this.changeCurrentItem(true);
    return true;
  }

  private onArrowUp() {
    this.changeCurrentItem(false);
    return true;
  }

  private onEnter(): boolean {
    const state = this.state;
    if (state.currentId !== '') {
      this.onAction(state.currentId);
    }
    return true;
  }

  private changeCurrentItem(next: boolean) {
    const state = this.state;
    const currentId = MenuTools.navigateMenu(state.menu, state.currentId, next);
    this.update(s => s.withCurrentId(currentId));
  }
}
